import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiControllerService } from '../../api-controller-service';
import { HttpErrorResponse } from '@angular/common/http';
import { REGEX_SECURITY_WEBSITE_URL } from 'src/app/shared/constants/app.constants';
import { parseHttpErrorMessage } from 'src/app/_helpers/http-error.utils';

@Component({
  selector: 'app-webhook-edit-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './webhook-edit-dialog.component.html',
  styleUrls: ['./webhook-edit-dialog.component.css']
})
export class WebhookEditDialogComponent implements OnInit, OnChanges {
  constructor(private readonly apiService: ApiControllerService) {}

  @Input() isVisible: boolean = false;
  @Input() saleUrl: string = '';
  @Input() refundUrl: string = '';
  @Input() mode: 'create' | 'edit' = 'edit';
  @Input() currentEnv: string = '';


  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() success = new EventEmitter<void>();
  @Output() failed = new EventEmitter<string>();

  // Internal form state (decoupled from @Input to avoid persisting unsaved edits)
  formSaleUrl: string = '';
  formRefundUrl: string = '';
  sameUrl: boolean = false;
  isSubmitting: boolean = false;

  // Live validation flags
  saleUrlError: string = '';
  refundUrlError: string = '';

  // merchant-validation state (create mode only)
  merchantQuery: string = '';
  isValidatingMerchant: boolean = false;
  merchantValidated: boolean = false;
  merchantError: string = '';
  validatedMerchant: { merchantId: string; merchantName: string } | null = null;
  @Input() merchantName: string = '';
  @Input() merchantId: string = '';
  @Input() showMerchantInfo: boolean = false;

  // HTTPS URL regex provided by request
  private static readonly httpsUrlRe = new RegExp(REGEX_SECURITY_WEBSITE_URL);
  

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reinitialize form when dialog opens OR when input URLs change while visible
    if (changes['isVisible'] && this.isVisible) {
      this.initializeForm();
    } else if ((changes['saleUrl'] || changes['refundUrl']) && !this.isVisible) {
      // Pre-load values when URLs change while dialog is closed
      this.formSaleUrl = this.saleUrl || '';
      this.formRefundUrl = this.refundUrl || '';
    }
  }

  get dialogTitle(): string {
     return this.mode === 'create' ? 'Add Webhooks' : 'Edit Webhooks';
  }

  get merchantDisplay(): string {
    const name = (this.merchantName || '').trim();
    const id = (this.merchantId || '').trim();
    if (name && id) return `${name} (${id})`;
    return name || id || '-';
  }

  get submitButtonText(): string {
    return 'Continue';
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  close(): void {
    this.isVisible = false;
    this.isVisibleChange.emit(false);
  }

  onToggleSameUrl(): void {
    this.sameUrl = !this.sameUrl;
    this.syncRefundIfNeeded();
    if (this.sameUrl) {
      this.refundUrlError = this.saleUrlError;
    }
  }

  onSaleUrlChange(): void {
    // Validate and propagate when needed
    this.saleUrlError = this.validateURLFormat(this.formSaleUrl || '');
    this.syncRefundIfNeeded();
    if (this.sameUrl) {
      this.refundUrlError = this.saleUrlError; // mirror
    }
  }

  private syncRefundIfNeeded(): void {
    if (this.sameUrl) {
      this.formRefundUrl = this.formSaleUrl || '';
    }
  }

  onRefundUrlChange(): void {
    if (this.sameUrl) {
      this.formRefundUrl = this.formSaleUrl || '';
      this.refundUrlError = this.saleUrlError;
      return;
    }
    this.refundUrlError = this.validateURLFormat(this.formRefundUrl || '');
  }

  private initializeForm(): void {
    // Load latest @Input values when opening
    this.formSaleUrl = this.saleUrl || '';
    this.formRefundUrl = this.refundUrl || '';
    this.sameUrl = (this.formSaleUrl?.trim().length ?? 0) > 0 && this.formSaleUrl === this.formRefundUrl;
    this.saleUrlError = '';
    this.refundUrlError = '';
    this.merchantValidated = false;
    this.merchantError = '';
    this.validatedMerchant = null;
    this.isValidatingMerchant = false;
    this.isSubmitting = false;
    // Create mode: reuse the merchant already searched on the main page
    if (this.mode === 'create' && this.merchantId) {
      this.merchantQuery = this.merchantDisplay;
      this.validatedMerchant = { merchantId: this.merchantId, merchantName: this.merchantName };
      this.merchantValidated = true;
    } else if(this.mode === 'edit' && this.merchantId) {
      this.merchantQuery = '';
      this.validatedMerchant = { merchantId: this.merchantId, merchantName: this.merchantName };
      this.merchantValidated = true;
    } else {
      this.merchantQuery = '';
    }
    // Initial validation states
    this.saleUrlError = this.validateURLFormat(this.formSaleUrl || '');
  
    if (this.sameUrl) {
      this.formRefundUrl = this.formSaleUrl || '';
      this.refundUrlError = this.saleUrlError;
    } else {
        this.refundUrlError = this.validateURLFormat(this.formRefundUrl || '');
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /** Validate URL format then return error message */
  private validateURLFormat(url: string): string {
    if(!WebhookEditDialogComponent.httpsUrlRe.test((url || '').trim())) {
      return 'Use a valid HTTPS';
    }
    return '';
  }

  isFormValid(): boolean {
    // Allow empty values, only check for validation errors if values are provided
    const merchantOk = this.mode === 'edit' || this.merchantValidated;
    const saleEmpty = !(this.formSaleUrl || '').trim();
    const refundEmpty = !(this.formRefundUrl || '').trim();
    if (saleEmpty && refundEmpty) return false;
    return merchantOk && !this.saleUrlError && !this.refundUrlError;
  }

  handleSubmit(): void {
    if (!this.isFormValid() || this.isSubmitting) return;
    this.isSubmitting = true;
    this.saleUrlError = '';
    this.refundUrlError = '';

    const request: any = {
      webhooks: {
        Sale: (this.formSaleUrl || '').trim(),
        Refund: (this.formRefundUrl || '').trim(),
      }
    };

    if (this.validatedMerchant) {
      request.merchantId = this.validatedMerchant.merchantId;
    }

    this.apiService.createOrUpdateWebhook(request as any).subscribe({
      next: (resp: any) => {
        this.isSubmitting = false;
        if (resp && (resp.status >= 400 || resp.message)) {
          this.saleUrlError = resp.message || 'Failed to save webhooks';
          this.refundUrlError = resp.message || 'Failed to save webhooks';
          return;
        }
        this.success.emit();
        this.close();
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        if (error?.status === 400) {
          this.saleUrlError = parseHttpErrorMessage(error, 'Invalid webhook URL(s)');
          this.refundUrlError = parseHttpErrorMessage(error, 'Invalid webhook URL(s)');
        } else {
          this.failed.emit(parseHttpErrorMessage(error, 'Failed to save webhooks'));
          this.close();
        }
      }
    });
  }

  onMerchantQueryChange() {
    // Any edit invalidates a previous successful validation
    if (this.merchantValidated) {
      this.merchantValidated = false;
      this.validatedMerchant = null;
    }
    this.merchantError = '';
  }

  searchMerchant() {
    const query = (this.merchantQuery || '').trim();
    if (!query || this.isValidatingMerchant) return;

    this.isValidatingMerchant = true;
    this.merchantError = '';
    this.merchantValidated = false;
    this.validatedMerchant = null;

    this.apiService.findMerchant(query).subscribe({
      next: (resp: any) => {
        this.isValidatingMerchant = false;
        const data = resp?.response?.data ?? resp?.data ?? null;
        if (data && data.merchantId) {
          this.merchantValidated = true;
          this.validatedMerchant = { merchantId: data.merchantId, merchantName: data.merchantName };
        } else {
          this.merchantValidated = false;
          this.validatedMerchant = null;
          this.merchantError = `No records found for '${query}'`;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isValidatingMerchant = false;
        this.merchantValidated = false;
        this.validatedMerchant = null;
        console.error('Error finding merchant:', err);
        this.merchantError = `No records found for '${query}'`;
      }
    });
  }
}
